import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { maps } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation, type TKey } from '../i18n';

type SvgView = 'full' | 'l0';

export default function MindmapPage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();
  const state = useApi(() => maps.byBook(bookId), [bookId]);
  const [view, setView] = useState<SvgView>('full');

  const layerLabel: Record<'L1' | 'L2' | 'L3' | 'L4', TKey> = {
    L1: 'map.surface', L2: 'map.argument', L3: 'map.concept', L4: 'map.meta',
  };

  // MindmapEngine v2가 만든 SVG URL — 있으면 우선 표시 (어린왕자 풍 일러스트 포함)
  const svgUrls = state.status === 'success' ? state.data.svg_urls : null;
  const svgSrc = svgUrls?.[view] || null;

  return (
    <div className="map-layout">
      <aside className="map-sidebar">
        <div className="sb-block">
          <div className="sb-title">{t('map.fourLayers')}</div>
          {(['L1', 'L2', 'L3', 'L4'] as const).map((L) => (
            <div key={L} className="layer-row">
              <span className="layer-swatch" style={{ background: `var(--bq-color-layer-${L.toLowerCase()})` }} />
              <span className="layer-label">
                <span className="layer-tag-l">{L}</span>
                {t(layerLabel[L])}
              </span>
              <span className="layer-count">—</span>
            </div>
          ))}
        </div>
        {svgUrls && (
          <div className="sb-block">
            <div className="sb-title">View</div>
            <div className="layer-row" style={{ gap: 8 }}>
              <button
                type="button"
                className={`topnav-link${view === 'full' ? ' active' : ''}`}
                onClick={() => setView('full')}
              >Full</button>
              <button
                type="button"
                className={`topnav-link${view === 'l0' ? ' active' : ''}`}
                onClick={() => setView('l0')}
              >L0 (5초)</button>
            </div>
          </div>
        )}
        <div className="sb-block">
          <div className="sb-title">{t('map.navigation')}</div>
          <Link to={`/books/${bookId}`} className="topnav-link">{t('common.back')}</Link>
        </div>
      </aside>

      <section className="map-canvas">
        {state.status === 'loading' && <p className="muted map-state-row">{t('map.loadingMap')}</p>}
        {state.status === 'error' && <p className="muted map-state-row">{t('map.notReady')}</p>}
        {state.status === 'success' && svgSrc && (
          <img
            src={svgSrc}
            alt={state.data.root_thesis || 'mindmap'}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
        {state.status === 'success' && !svgSrc && (
          // SVG가 아직 안 만들어졌으면 L1 노드 그리드 fallback
          <>
            <div className="map-center">
              <em>{state.data.root_thesis || 'untitled'}</em>
            </div>
            {(state.data.nodes ?? []).slice(0, 12).map((n, i) => {
              const x = 15 + (i % 4) * 23;
              const y = 24 + Math.floor(i / 4) * 28;
              const cls = n.layer ? n.layer.toLowerCase() : 'l1';
              return (
                <div key={n.node_id} className={`mnode ${cls}`} style={{ top: `${y}%`, left: `${x}%` }}>
                  <span className="ltag">{n.layer}</span>{n.label}
                </div>
              );
            })}
          </>
        )}
      </section>

      <aside className="map-inspector">
        <div className="ins-tags"><span className="ins-tag ins-tag-mute">{t('map.hoverHint')}</span></div>
        <h2 className="ins-title">{t('map.selectNode')}</h2>
        <p className="ins-sub">{t('map.selectNodeSub')}</p>
      </aside>
    </div>
  );
}
